/**
 * Flashcard hint logic — meaning-based semantic clues in the language of the current side.
 * - Front: short fallback in front language (e.g. "Think of the meaning.").
 * - Back: card.hint, card.definition, or fallback in back language.
 * Replaces masked-letter hints with short explanatory hints.
 */

import type { Card } from "@/types/linguadeck";

/** Check if a string looks like a masked-letter pattern (e.g. "D__i__on"), not a semantic hint. */
function looksLikeMaskedHint(s: string): boolean {
  if (!s || s.length < 3) return false;
  const underscoreCount = (s.match(/_/g) || []).length;
  return underscoreCount >= s.length * 0.15;
}

/** Short fallback message in back-side language when no semantic hint exists. */
const HINT_FALLBACK_BY_LANG: Record<string, string> = {
  en: "Think of the meaning.",
  es: "Piensa en el significado.",
  fr: "Pensez au sens.",
  de: "Denke an die Bedeutung.",
  it: "Pensa al significato.",
  nl: "Denk aan de betekenis.",
  pl: "Pomyśl o znaczeniu.",
  "pt-BR": "Pense no significado.",
  tr: "Anlamı düşün.",
  ko: "의미를 떠올려 보세요.",
  ru: "Подумайте о значении.",
  uk: "Подумай про значення.",
};

/**
 * Get the front-side (question) language for a card.
 */
export function getFrontSideLang(card: Card, deckLang: string): string {
  const raw = (card.frontLang ?? "").toString().trim();
  return raw || deckLang;
}

/**
 * Get the back-side (answer) language for a card.
 * Used to display hints in the learner's understanding language.
 */
export function getBackSideLang(card: Card, deckLang: string): string {
  const raw = (card.backLang ?? "").toString().trim();
  return raw || deckLang;
}

function getFallbackForLang(lang: string): string {
  return (
    HINT_FALLBACK_BY_LANG[lang] ??
    HINT_FALLBACK_BY_LANG[lang.split("-")[0] ?? ""] ??
    HINT_FALLBACK_BY_LANG.en
  );
}

/**
 * Returns a short meaning-based hint in the language of the given side.
 * - Front: fallback only in front language (card.hint/definition are typically in back language).
 * - Back: card.hint or card.definition or fallback in back language.
 *
 * Never returns masked-letter patterns.
 */
export function getMeaningBasedHint(
  card: Card,
  deckLang: string,
  side: "front" | "back"
): string {
  const frontLang = getFrontSideLang(card, deckLang);
  const backLang = getBackSideLang(card, deckLang);

  if (side === "front") {
    return getFallbackForLang(frontLang);
  }

  // Back side: use hint/definition/fallback in back language
  const rawHint = (card.hint ?? "").trim();
  if (rawHint && !looksLikeMaskedHint(rawHint)) {
    return truncateHint(rawHint);
  }
  const definition = (card.definition ?? "").trim();
  if (definition) {
    return truncateHint(definition);
  }
  return getFallbackForLang(backLang);
}

/** Keep hints short: one phrase, max ~120 chars. */
function truncateHint(s: string, maxLen = 120): string {
  if (!s) return "";
  const trimmed = s.trim();
  if (trimmed.length <= maxLen) return trimmed;
  const cut = trimmed.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(" ");
  if (lastSpace > maxLen * 0.6) return cut.slice(0, lastSpace).trim() + "…";
  return cut.trim() + "…";
}
